import { Capacitor } from "@capacitor/core";
import type { StoreBusinessAccess } from "@wenjing/contract/store-management";
import { getApiBase } from './api-base.mjs';
import { syncServiceDisplayClock } from "./service-time";
export { getApiBase };

export type User = { id: string; tenantId: string; storeId: string | null; role: string; name: string; phone: string; dataScope: string; tenantName: string; storeName: string | null; subscriptionStatus: string | null; planCode: string | null; offboardingStatus?: string | null; permissions?: string[]; businessAccess?: StoreBusinessAccess };

const TOKEN_KEY = "wenjing_token";
const API_KEY = "wenjing_api_base";
export class ApiError extends Error { constructor(message: string, public readonly status: number, public readonly code = "") { super(message); } }

// Older API processes return scan findings without an error field. Keep those actionable.
export function apiErrorMessage(data: unknown, status: number) {
  const payload = data && typeof data === "object" ? data as Record<string, unknown> : {};
  if (typeof payload.error === "string" && payload.error.trim()) return payload.error;
  if (payload.submitted === false && Array.isArray(payload.findings)) {
    const reasons = payload.findings.map(item => item && typeof item.message === "string" ? item.message : "").filter(Boolean);
    if (reasons.length) return "合规扫描未通过：" + reasons.join("；") + "。请在治理档案中核对素材与扫描记录，修正后重新提交。";
  }
  return `请求失败：${status}`;
}

export function getToken() { return localStorage.getItem(TOKEN_KEY) || ""; }
export function setToken(value: string) { value ? localStorage.setItem(TOKEN_KEY, value) : localStorage.removeItem(TOKEN_KEY); }
export function setApiBase(value: string) { value ? localStorage.setItem(API_KEY, value.replace(/\/$/, "")) : localStorage.removeItem(API_KEY); }

function requestHeaders(options: RequestInit) {
  let idempotencyKey = "";
  if (typeof options.body === "string") {
    try { const payload = JSON.parse(options.body) as Record<string, unknown>; if (typeof payload.idempotencyKey === "string") idempotencyKey = payload.idempotencyKey; }
    catch { /* Non-JSON request bodies are handled by the caller. */ }
  }
  return {
    "Content-Type": "application/json",
    ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
    ...(options.headers || {})
  };
}

function canUseCurrentSiteFallback(apiBase: string) {
  if (Capacitor.isNativePlatform()) return false;
  if (!apiBase || typeof window === "undefined" || !["http:", "https:"].includes(window.location.protocol)) return false;
  try { return new URL(apiBase, window.location.href).origin !== window.location.origin; }
  catch { return true; }
}

async function fetchWithFallbackTimeout(url: string, request: RequestInit, enabled: boolean) {
  if (!enabled || request.signal) return fetch(url, request);
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 5000);
  try { return await fetch(url, { ...request, signal: controller.signal }); }
  finally { window.clearTimeout(timer); }
}

export async function api<T = unknown>(path: string, options: RequestInit = {}, behavior: {allowFallback?:boolean} = {}): Promise<T> {
  const apiBase = getApiBase();
  const request = { ...options, headers: requestHeaders(options) };
  const fallbackAllowed = behavior.allowFallback !== false && canUseCurrentSiteFallback(apiBase);
  let response: Response;
  try {
    response = await fetchWithFallbackTimeout(`${apiBase}${path}`, request, fallbackAllowed);
  } catch (error) {
    if (!fallbackAllowed) throw new ApiError(error instanceof Error ? `Server connection failed: ${error.message}` : "Server connection failed", 0, "NETWORK_ERROR");
    response = await fetch(path, request);
    setApiBase("");
  }
  if (fallbackAllowed && (response.status === 404 || !String(response.headers.get("content-type") || "").toLowerCase().includes("application/json"))) {
    response = await fetch(path, request);
    setApiBase("");
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(apiErrorMessage(data, response.status), response.status, String(data?.code || ""));
  syncServiceDisplayClock(response.headers.get("date"));
  return data as T;
}

export function yuan(cents: unknown) {
  return `¥${(Number(cents || 0) / 100).toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export const roleNames: Record<string, string> = {
  platform_admin: "平台管理员", company_admin: "公司管理员", employee: "员工",
  worker: "服务人员", customer: "客户"
};
